import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import {
  walletAddressStatus,
  walletTypeColor,
  walletAddressTypeImage,
} from "@lib/helpers/ColorHelper";
import { shortenString } from "@lib/helpers/StringHelper";
import {
  WalletAddress,
  WalletAddressStatus,
  WalletType,
} from "@lib/types/Billing";
import { Tag, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { ApiParams, UseMutationProps } from "@lib/types/Api";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { TableColumnsProps } from "@lib/types/General";
import { ROUTES } from "@lib/constants";
// import { AppContext } from "@lib/types";
import {
  WalletAddressText,
  CustomerName,
  CoinPriceTextFormat,
} from "@lib/components/MiniComponents/Others";

type Props<T> = TableColumnsProps<T> & {
  openModal: boolean;
  handleSelectAddress: (walletAddress: T) => void;
  deleteQuery: UseMutationProps<T>;
  addScanQuery: UseMutationProps<any>;
  setParams?: (params: Partial<ApiParams>) => void;
  setFilter?: (filter: string) => void;
};

const getColumns = <T extends WalletAddress>(
  props: Props<T>,
): ColumnsType<T> => {
  const {
    detailContext,
    messages,
    handleSelectAddress,
    deleteQuery,
    addScanQuery,
    navigate,
  } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "common.walletAddress",
      dataIndex: "address",
      width: "160px",
      render: (_, r) => <WalletAddressText walletAddress={r} />,
      fixed: "left",
      filterType: "search",
    },
    {
      title: "common.customer",
      dataIndex: "customer",
      width: "180px",
      render: (v, r) => (
        <CustomerName
          customer={r?.customerUserId ? r.customerUser?.customer : r?.customer}
          link
        />
      ),
      hidden: !!detailContext,
      // hiddenFor: [AppContext.CUSTOMER_LEAD],
    },
    {
      title: "common.balance",
      dataIndex: "balance",
      width: "150px",
      align: "right",
      render: (value, r) => (
        <CoinPriceTextFormat amount={value} currency={r?.network} />
      ),
    },
    {
      title: "common.remainingBalanceShort",
      dataIndex: "remainingBalance",
      width: "150px",
      align: "right",
      render: (value, r) => (
        <CoinPriceTextFormat amount={value} currency={r?.network} />
      ),
    },
    {
      title: "common.walletNetwork",
      dataIndex: "network",
      render: (v, r) => (
        <StyledFlex style={{ gap: 3 }}>
          <img
            src={walletAddressTypeImage(r.network)}
            style={{ maxHeight: "20px" }}
          />
          {r.network}
        </StyledFlex>
      ),
      width: "130px",
      filterType: "dropdown",
    },
    {
      title: "common.walletAddressStatus",
      dataIndex: "status",
      width: "160px",
      render: (value) => (
        <Tag color={walletAddressStatus(value)}>
          {(messages["enums.walletAddressStatus"][value] as string) || value}
        </Tag>
      ),
      filterType: "dropdown",
      filters: Object.values(WalletAddressStatus).map((value) => ({
        text: messages["enums.walletAddressStatus"][value] || value,
        value: value,
      })),
    },
    {
      title: "common.lastScanDate",
      dataIndex: "lastScanDate",
      width: "180px",

      render: (d) => d && dayjs(d).fromNow(),
    },
    {
      title: "common.walletId",
      dataIndex: "customId",
      width: "115px",
      align: "right",

      filterType: "search",
    },
    {
      title: "common.walletName",
      dataIndex: "wallet.name",
      width: "150px",
      render: (v, r) => (
        <Tooltip title={r?.wallet?.name}>
          <Typography.Text>
            {shortenString(r?.wallet?.name, 13)}
          </Typography.Text>
        </Tooltip>
      ),
      filterType: "search",
    },
    {
      title: "common.walletType",
      dataIndex: "wallet.type",
      width: "150px",
      render: (u, r) => (
        <Tag color={walletTypeColor(r?.wallet?.type)}>
          {messages["walletType"][r?.wallet?.type] as string}
        </Tag>
      ),
      filterType: "dropdown",
      filters: Object.values(WalletType).map((item) => ({
        text: messages["walletType"][item],
        value: item,
      })),
    },
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "180px",
      render: (d) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "common.createdBy",
      dataIndex: "createdBy",
      width: "180px",
      filterType: "dropdown",
    },
    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      width: "180px",
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "common.updatedBy",
      dataIndex: "updatedBy",
      width: "180px",
      filterType: "dropdown",
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      fixed: "right",
      width: "140px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="scan"
            onClick={() => addScanQuery.mutate(record.id)}
            noLabel
            loading={addScanQuery.isPending}
          />

          <ActionButton
            type="edit"
            onClick={() => handleSelectAddress(record)}
            noLabel
          />

          <ActionButton
            type="history"
            onClick={() => {
              navigate(ROUTES.BLOCKCHAIN_PAYMENT_REQUEST.LIST, {
                state: { walletAddress: record.address },
              });
            }}
            noLabel
          />

          <ActionButton
            type="delete"
            onClick={() => deleteQuery.mutate(record)}
            itemName="common.walletAddress"
            noLabel
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};
export default getColumns;
